import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts";
import ThemedChartTooltip, { ThemedTooltipBox } from "../../shared/ThemedChartTooltip";
import InfoTip from "../../shared/InfoTip";
import { BRAND_BLUE } from "../../shared/colors";

/**
 * ExpectedVsActual — weather_adjusted_performance evidence component.
 *
 * The most visually important panel below the pinned strip (spec §7).
 * Three figures: Expected ignoring weather → Expected given weather → Actual.
 * Lead with performance_vs_weather_expectation_pct as a large signed number.
 *
 * Spec references: §7 (weather_adjusted_performance).
 */

/** Format number with locale separators. */
const fmtNum = (v) => Number(v ?? 0).toLocaleString();

const ExpectedVsActual = ({ panel }) => {
  const f = panel.facts || {};

  const expectedIgnoring = Number(f.expected_ignoring_weather) || 0;
  const expectedGiven = Number(f.expected_given_weather) || 0;
  const actual = Number(f.actual_bookings) || 0;
  const weatherEffect = Number(f.weather_effect_bookings) || 0;
  const perfPct = Number(f.performance_vs_weather_expectation_pct) || 0;

  const isPositive = perfPct >= 0;
  const perfColor = isPositive
    ? "text-emerald-600 dark:text-emerald-400"
    : "text-red-500";

  const weatherEffectLabel =
    weatherEffect < 0
      ? `Weather cost about ${fmtNum(Math.abs(weatherEffect))} bookings`
      : weatherEffect > 0
        ? `Weather added about ${fmtNum(weatherEffect)} bookings`
        : null;

  const chartData = [
    { label: "Expected\nignoring weather", value: expectedIgnoring, color: "#9CA3AF" },
    { label: "Expected\ngiven weather", value: expectedGiven, color: "#60A5FA" },
    { label: "Actual", value: actual, color: BRAND_BLUE },
  ];

  return (
    <div className="space-y-4">
      {/* ─── Hero number + weather effect — side by side ──────────────── */}
      <div className="flex items-end justify-between gap-4">
        <div className="flex items-baseline gap-2">
          <span className={`text-3xl font-bold leading-none ${perfColor}`}>
            {isPositive ? "+" : ""}{perfPct}%
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            vs weather expectation
          </span>
        </div>
        {weatherEffectLabel && (
          <p className="text-sm text-gray-600 dark:text-gray-400 text-right">
            {weatherEffectLabel}
          </p>
        )}
      </div>

      {/* ─── Three-bar chart (in a card, like GA ChartCard) ──────────── */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <span>Bookings: Expected vs Actual</span>
            <InfoTip text="Compares what you'd expect ignoring weather, what the weather predicts, and what actually happened. The gap between the first two bars is the weather effect; the gap between the last two is your performance." />
          </h3>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={{ stroke: "#374151" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              width={45}
              allowDecimals={false}
            />
            <ThemedChartTooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <ThemedTooltipBox title={label.replace(/\n/g, " ")}>
                    <div>Bookings: {fmtNum(payload[0].value)}</div>
                  </ThemedTooltipBox>
                );
              }}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={48}>
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ExpectedVsActual;